import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTenant } from '../../contexts/TenantContext';
import { productionApi } from '../../api/production.api';
import type { CropPlan, Crop, CropTask, Variety, Plot } from '../../types/production.types';

// ============================================================================
// Constants
// ============================================================================

const CROP_STATUS_LABELS: Record<string, string> = {
  PLANNED: 'Planifie', SEEDED: 'Seme', GROWING: 'En croissance',
  HARVESTING: 'En recolte', COMPLETED: 'Termine', FAILED: 'Echec',
};
const CROP_STATUS_COLORS: Record<string, string> = {
  PLANNED: 'bg-gray-100 text-gray-700', SEEDED: 'bg-blue-100 text-blue-700',
  GROWING: 'bg-green-100 text-green-700', HARVESTING: 'bg-yellow-100 text-yellow-700',
  COMPLETED: 'bg-gray-200 text-gray-600', FAILED: 'bg-red-100 text-red-700',
};

const TASK_TYPE_LABELS: Record<string, string> = {
  SEEDING: 'Semis', TRANSPLANT: 'Repiquage', WATERING: 'Arrosage',
  WEEDING: 'Desherbage', HARVEST: 'Recolte', TREATMENT: 'Traitement',
  FERTILIZING: 'Fertilisation', PRUNING: 'Taille', MULCHING: 'Paillage',
  HILLING: 'Buttage', NETTING: 'Filet protection', STAKING: 'Tuteurage',
  THINNING: 'Eclaircissage', OTHER: 'Autre',
};

const TASK_TYPE_COLORS: Record<string, string> = {
  SEEDING: 'bg-blue-100 text-blue-800',
  TRANSPLANT: 'bg-violet-100 text-violet-800',
  HARVEST: 'bg-green-100 text-green-800',
  WATERING: 'bg-cyan-100 text-cyan-800',
  WEEDING: 'bg-rose-100 text-rose-800',
  TREATMENT: 'bg-red-100 text-red-800',
  FERTILIZING: 'bg-amber-100 text-amber-800',
  PRUNING: 'bg-orange-100 text-orange-800',
  MULCHING: 'bg-lime-100 text-lime-800',
  HILLING: 'bg-stone-200 text-stone-800',
  NETTING: 'bg-indigo-100 text-indigo-800',
  STAKING: 'bg-teal-100 text-teal-800',
  THINNING: 'bg-sky-100 text-sky-800',
  OTHER: 'bg-gray-100 text-gray-700',
};

type TaskFilter = 'all' | 'pending' | 'completed';

// ============================================================================
// Helpers
// ============================================================================

function getISOWeek(date: Date): number {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
  const week1 = new Date(d.getFullYear(), 0, 4);
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7);
}

function getWeekDateRange(year: number, week: number): { start: Date; end: Date } {
  const jan4 = new Date(year, 0, 4);
  const dayOfWeek = jan4.getDay() || 7;
  const monday = new Date(jan4);
  monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return { start: monday, end: sunday };
}

function formatDateShort(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-BE', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatDateOnly(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-BE', { day: '2-digit', month: '2-digit' });
}

// ============================================================================
// Main Component
// ============================================================================

export default function CropPlanning() {
  const { t } = useTranslation();
  const { tenant, loading: tenantLoading } = useTenant();

  const [plans, setPlans] = useState<CropPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<CropPlan | null>(null);
  const [crops, setCrops] = useState<Crop[]>([]);
  const [tasks, setTasks] = useState<CropTask[]>([]);
  const [varieties, setVarieties] = useState<Variety[]>([]);
  const [plots, setPlots] = useState<Plot[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'crops' | 'tasks'>('crops');

  // Task view state
  const [taskFilter, setTaskFilter] = useState<TaskFilter>('all');
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);
  const [completionNote, setCompletionNote] = useState('');
  const [reschedulingTaskId, setReschedulingTaskId] = useState<string | null>(null);
  const [rescheduleDate, setRescheduleDate] = useState('');

  // Crop modal
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropForm, setCropForm] = useState({
    variety_id: '', plot_id: '', planned_seed_date: '', quantity_planted: '', area_used_m2: '', notes: '',
  });

  // Harvest modal
  const [harvestModalOpen, setHarvestModalOpen] = useState(false);
  const [harvestCropId, setHarvestCropId] = useState('');
  const [harvestForm, setHarvestForm] = useState({ harvest_date: new Date().toISOString().split('T')[0], quantity_kg: '', quality_grade: 'A', notes: '' });

  const currentYear = new Date().getFullYear();
  const currentWeek = getISOWeek(new Date());

  useEffect(() => {
    if (!tenantLoading && tenant) loadInitial();
  }, [tenant, tenantLoading]);

  // Set current week when switching to tasks view
  useEffect(() => {
    if (view === 'tasks' && selectedWeek === null) {
      setSelectedWeek(currentWeek);
    }
  }, [view]);

  const loadInitial = async () => {
    try {
      setLoading(true);
      const [plansData, varietiesData, plotsData] = await Promise.all([
        productionApi.listCropPlans(),
        productionApi.listVarieties(),
        productionApi.listPlots(),
      ]);
      setPlans(plansData);
      setVarieties(varietiesData);
      setPlots(plotsData);

      const current = plansData.find((p) => p.year === currentYear) || plansData[0];
      if (current) {
        setSelectedPlan(current);
        await loadPlanData(current.id);
      }
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadPlanData = async (planId: string) => {
    try {
      const [cropsData, tasksData] = await Promise.all([
        productionApi.listCrops({ crop_plan_id: planId }),
        productionApi.listCropTasks(),
      ]);
      setCrops(cropsData);
      setTasks(tasksData.filter((t) => cropsData.some((c) => c.id === t.crop_id)));
    } catch (err: any) {
      console.error(err);
    }
  };

  const selectPlan = async (planId: string) => {
    const plan = plans.find((p) => p.id === planId);
    if (plan) {
      setSelectedPlan(plan);
      await loadPlanData(plan.id);
    }
  };

  const createPlan = async () => {
    try {
      const plan = await productionApi.createCropPlan({ year: currentYear, name: `Plan ${currentYear}` });
      setPlans([plan, ...plans]);
      setSelectedPlan(plan);
      setCrops([]);
      setTasks([]);
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  const saveCrop = async () => {
    if (!selectedPlan) return;
    try {
      const data: any = {
        crop_plan_id: selectedPlan.id,
        variety_id: cropForm.variety_id,
        plot_id: cropForm.plot_id,
        notes: cropForm.notes || null,
      };
      if (cropForm.planned_seed_date) data.planned_seed_date = new Date(cropForm.planned_seed_date);
      if (cropForm.quantity_planted) data.quantity_planted = parseInt(cropForm.quantity_planted);
      if (cropForm.area_used_m2) data.area_used_m2 = parseFloat(cropForm.area_used_m2);

      await productionApi.createCrop(data);
      setCropModalOpen(false);
      loadPlanData(selectedPlan.id);
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  // ── Task actions ──────────────────────────────────────────

  const handleCompleteTask = async (taskId: string) => {
    try {
      // Save completion note if provided
      if (completionNote.trim()) {
        await productionApi.updateCropTask(taskId, { description: completionNote.trim() } as any);
      }
      await productionApi.completeCropTask(taskId);
      setCompletingTaskId(null);
      setCompletionNote('');
      if (selectedPlan) loadPlanData(selectedPlan.id);
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  const handleRescheduleTask = async (taskId: string) => {
    if (!rescheduleDate) return;
    try {
      const newDate = new Date(rescheduleDate);
      const newWeek = getISOWeek(newDate);
      await productionApi.updateCropTask(taskId, {
        planned_date: rescheduleDate,
        planned_week: newWeek,
      } as any);
      setReschedulingTaskId(null);
      setRescheduleDate('');
      if (selectedPlan) loadPlanData(selectedPlan.id);
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  const saveHarvest = async () => {
    try {
      await productionApi.createHarvest({
        crop_id: harvestCropId,
        harvest_date: harvestForm.harvest_date,
        quantity_kg: parseFloat(harvestForm.quantity_kg),
        quality_grade: harvestForm.quality_grade,
        notes: harvestForm.notes || undefined,
      });
      setHarvestModalOpen(false);
      if (selectedPlan) loadPlanData(selectedPlan.id);
    } catch (err: any) {
      alert('Erreur: ' + err.message);
    }
  };

  const openHarvestModal = (cropId: string) => {
    setHarvestCropId(cropId);
    setHarvestForm({ harvest_date: new Date().toISOString().split('T')[0], quantity_kg: '', quality_grade: 'A', notes: '' });
    setHarvestModalOpen(true);
  };

  // ── Task grouping by week ─────────────────────────────────

  const filteredTasks = useMemo(() => {
    let filtered = tasks;
    if (taskFilter === 'pending') filtered = filtered.filter((t) => !t.completed_at);
    if (taskFilter === 'completed') filtered = filtered.filter((t) => !!t.completed_at);
    return filtered;
  }, [tasks, taskFilter]);

  const tasksByWeek = useMemo(() => {
    const grouped: Record<number, CropTask[]> = {};
    filteredTasks.forEach((t) => {
      const week = t.planned_week || 0;
      if (!grouped[week]) grouped[week] = [];
      grouped[week].push(t);
    });
    // Sort tasks within each week by planned_date
    Object.values(grouped).forEach((weekTasks) => {
      weekTasks.sort((a, b) => {
        const dateA = a.planned_date ? new Date(a.planned_date).getTime() : 0;
        const dateB = b.planned_date ? new Date(b.planned_date).getTime() : 0;
        return dateA - dateB;
      });
    });
    return grouped;
  }, [filteredTasks]);

  const availableWeeks = useMemo(() => {
    return Object.keys(tasksByWeek).map(Number).sort((a, b) => a - b);
  }, [tasksByWeek]);

  // Navigate to next/prev week that has tasks
  const navigateWeek = (direction: 'prev' | 'next') => {
    if (!selectedWeek) return;
    if (direction === 'next') {
      const next = availableWeeks.find((w) => w > selectedWeek);
      if (next) setSelectedWeek(next);
    } else {
      const prev = [...availableWeeks].reverse().find((w) => w < selectedWeek);
      if (prev) setSelectedWeek(prev);
    }
  };

  const pendingCount = tasks.filter((t) => !t.completed_at).length;
  const completedCount = tasks.filter((t) => !!t.completed_at).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold text-gray-900">
          {t('production.cropPlans.title', 'Plan de culture')}
        </h1>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 min-w-0 max-w-full">
          {plans.length > 0 ? (
            <select className="input min-w-0 max-w-full" value={selectedPlan?.id || ''} onChange={(e) => selectPlan(e.target.value)}>
              {plans.map((p) => <option key={p.id} value={p.id}>{p.name || `Plan ${p.year}`} ({p.status})</option>)}
            </select>
          ) : null}
          <button onClick={createPlan} className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm">
            + Nouveau plan
          </button>
        </div>
      </div>

      {!selectedPlan ? (
        <div className="bg-white rounded-lg shadow p-8 text-center text-gray-400">
          Aucun plan de culture. Creez-en un pour commencer.
        </div>
      ) : (
        <>
          {/* View toggle */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button onClick={() => setView('crops')} className={`px-4 py-2 text-sm rounded-md ${view === 'crops' ? 'bg-white shadow font-medium' : 'text-gray-600'}`}>
              Cultures ({crops.length})
            </button>
            <button onClick={() => setView('tasks')} className={`px-4 py-2 text-sm rounded-md ${view === 'tasks' ? 'bg-white shadow font-medium' : 'text-gray-600'}`}>
              Taches ({pendingCount} en attente)
            </button>
          </div>

          {/* Add crop button */}
          {view === 'crops' && (
            <button
              onClick={() => {
                setCropForm({ variety_id: '', plot_id: '', planned_seed_date: '', quantity_planted: '', area_used_m2: '', notes: '' });
                setCropModalOpen(true);
              }}
              className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-4 w-full text-center text-gray-500 hover:border-green-500 hover:text-green-600"
            >
              + Ajouter une culture
            </button>
          )}

          {/* ══════════════════════════════════════════════════════════ */}
          {/* Crops view */}
          {/* ══════════════════════════════════════════════════════════ */}
          {view === 'crops' && (
            <div className="space-y-3">
              {crops.map((crop) => (
                <div key={crop.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-semibold">{(crop.variety as any)?.species} — {(crop.variety as any)?.name}</span>
                      <span className={`ml-2 text-xs px-2 py-0.5 rounded ${CROP_STATUS_COLORS[crop.status]}`}>
                        {CROP_STATUS_LABELS[crop.status]}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {crop.status !== 'COMPLETED' && crop.status !== 'FAILED' && (
                        <button onClick={() => openHarvestModal(crop.id)} className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded hover:bg-green-200">
                          Recolter
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                    <span>{(crop.plot as any)?.code}{(crop.plot as any)?.zone && ` (${(crop.plot as any).zone.name})`}</span>
                    {crop.planned_seed_date && <span>Semis: {new Date(crop.planned_seed_date).toLocaleDateString('fr-BE')}</span>}
                    {crop.quantity_planted && <span>{crop.quantity_planted} plants</span>}
                    {crop.actual_yield_kg && <span>{crop.actual_yield_kg} kg recoltes</span>}
                  </div>
                  {crop.tasks && crop.tasks.length > 0 && (
                    <div className="mt-2 flex gap-1 flex-wrap">
                      {crop.tasks.map((task) => (
                        <span
                          key={task.id}
                          className={`text-xs px-1.5 py-0.5 rounded ${task.completed_at ? 'bg-gray-100 text-gray-400 line-through' : TASK_TYPE_COLORS[task.type] || 'bg-green-50 text-green-700'}`}
                        >
                          {TASK_TYPE_LABELS[task.type] || task.type}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {crops.length === 0 && (
                <div className="text-center text-gray-400 py-8">Aucune culture dans ce plan</div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════ */}
          {/* Tasks view — refonte terrain */}
          {/* ══════════════════════════════════════════════════════════ */}
          {view === 'tasks' && (
            <div className="space-y-4">
              {/* Toolbar: filter + week navigation */}
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
                {/* Filter toggle */}
                <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
                  <button
                    onClick={() => setTaskFilter('all')}
                    className={`px-3 py-1.5 text-sm rounded-md ${taskFilter === 'all' ? 'bg-white shadow font-medium' : 'text-gray-600'}`}
                  >
                    Toutes ({tasks.length})
                  </button>
                  <button
                    onClick={() => setTaskFilter('pending')}
                    className={`px-3 py-1.5 text-sm rounded-md ${taskFilter === 'pending' ? 'bg-white shadow font-medium' : 'text-gray-600'}`}
                  >
                    En attente ({pendingCount})
                  </button>
                  <button
                    onClick={() => setTaskFilter('completed')}
                    className={`px-3 py-1.5 text-sm rounded-md ${taskFilter === 'completed' ? 'bg-white shadow font-medium' : 'text-gray-600'}`}
                  >
                    Terminees ({completedCount})
                  </button>
                </div>

                {/* Week navigation */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigateWeek('prev')}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                    disabled={!availableWeeks.length || selectedWeek === availableWeeks[0]}
                  >
                    &larr;
                  </button>
                  <button
                    onClick={() => setSelectedWeek(currentWeek)}
                    className={`px-3 py-1.5 text-sm rounded-lg ${selectedWeek === currentWeek ? 'bg-green-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  >
                    Cette semaine (S{currentWeek})
                  </button>
                  <button
                    onClick={() => setSelectedWeek(null)}
                    className={`px-3 py-1.5 text-sm rounded-lg ${selectedWeek === null ? 'bg-green-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                  >
                    Toutes
                  </button>
                  <button
                    onClick={() => navigateWeek('next')}
                    className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                    disabled={!availableWeeks.length || selectedWeek === availableWeeks[availableWeeks.length - 1]}
                  >
                    &rarr;
                  </button>
                </div>
              </div>

              {/* Week quick select */}
              <div className="flex gap-1 flex-wrap">
                {availableWeeks.map((w) => {
                  const weekPending = (tasksByWeek[w] || []).filter((t) => !t.completed_at).length;
                  const isCurrentWeek = w === currentWeek;
                  const isSelected = w === selectedWeek;
                  return (
                    <button
                      key={w}
                      onClick={() => setSelectedWeek(isSelected ? null : w)}
                      className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                        isSelected
                          ? 'bg-green-600 text-white border-green-600'
                          : isCurrentWeek
                            ? 'bg-green-50 text-green-800 border-green-300 hover:bg-green-100'
                            : weekPending > 0
                              ? 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100'
                      }`}
                    >
                      S{w}{weekPending > 0 && ` (${weekPending})`}
                    </button>
                  );
                })}
              </div>

              {/* Task cards grouped by week */}
              {(selectedWeek !== null ? [selectedWeek] : availableWeeks).map((week) => {
                const weekTasks = tasksByWeek[week];
                if (!weekTasks || weekTasks.length === 0) {
                  if (selectedWeek !== null) {
                    return (
                      <div key={week} className="text-center text-gray-400 py-8">
                        Aucune tache pour la semaine {week}
                      </div>
                    );
                  }
                  return null;
                }

                const { start, end } = getWeekDateRange(currentYear, week);
                const weekLabel = `${start.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-BE', { day: 'numeric', month: 'short' })}`;
                const isCurrentW = week === currentWeek;

                return (
                  <div key={week}>
                    {/* Week header */}
                    <div className={`flex items-center gap-3 py-2 px-1 mb-2 border-b-2 ${isCurrentW ? 'border-green-500' : 'border-gray-300'}`}>
                      <span className={`text-lg font-bold ${isCurrentW ? 'text-green-700' : 'text-gray-700'}`}>
                        Semaine {week}
                      </span>
                      <span className="text-sm text-gray-500">({weekLabel})</span>
                      {isCurrentW && (
                        <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">
                          cette semaine
                        </span>
                      )}
                      <span className="text-xs text-gray-400 ml-auto">
                        {weekTasks.filter((t) => !t.completed_at).length} en attente / {weekTasks.length} total
                      </span>
                    </div>

                    {/* Task cards */}
                    <div className="space-y-2">
                      {weekTasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          isCompleting={completingTaskId === task.id}
                          completionNote={completingTaskId === task.id ? completionNote : ''}
                          isRescheduling={reschedulingTaskId === task.id}
                          rescheduleDate={reschedulingTaskId === task.id ? rescheduleDate : ''}
                          onStartComplete={() => {
                            setCompletingTaskId(task.id);
                            setCompletionNote('');
                            setReschedulingTaskId(null);
                          }}
                          onCancelComplete={() => {
                            setCompletingTaskId(null);
                            setCompletionNote('');
                          }}
                          onConfirmComplete={() => handleCompleteTask(task.id)}
                          onCompletionNoteChange={setCompletionNote}
                          onStartReschedule={() => {
                            setReschedulingTaskId(task.id);
                            setRescheduleDate(task.planned_date ? task.planned_date.split('T')[0] : '');
                            setCompletingTaskId(null);
                          }}
                          onCancelReschedule={() => {
                            setReschedulingTaskId(null);
                            setRescheduleDate('');
                          }}
                          onConfirmReschedule={() => handleRescheduleTask(task.id)}
                          onRescheduleDateChange={setRescheduleDate}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}

              {filteredTasks.length === 0 && (
                <div className="text-center text-gray-400 py-8">
                  {taskFilter === 'completed' ? 'Aucune tache terminee' : taskFilter === 'pending' ? 'Toutes les taches sont terminees' : 'Aucune tache'}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Crop Modal */}
      {cropModalOpen && (
        <Modal title="Nouvelle culture" onClose={() => setCropModalOpen(false)}>
          <div className="space-y-3">
            <select className="input" value={cropForm.variety_id} onChange={(e) => setCropForm({ ...cropForm, variety_id: e.target.value })}>
              <option value="">Choisir une variete</option>
              {varieties.map((v) => <option key={v.id} value={v.id}>{v.species} - {v.name}</option>)}
            </select>
            <select className="input" value={cropForm.plot_id} onChange={(e) => setCropForm({ ...cropForm, plot_id: e.target.value })}>
              <option value="">Choisir une planche</option>
              {plots.map((p) => <option key={p.id} value={p.id}>{p.code}{p.zone ? ` (${p.zone.name})` : ''} — {p.area_m2} m2</option>)}
            </select>
            <input className="input" type="date" value={cropForm.planned_seed_date} onChange={(e) => setCropForm({ ...cropForm, planned_seed_date: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input className="input" type="number" placeholder="Nb plants" value={cropForm.quantity_planted} onChange={(e) => setCropForm({ ...cropForm, quantity_planted: e.target.value })} />
              <input className="input" type="number" placeholder="Surface (m2)" value={cropForm.area_used_m2} onChange={(e) => setCropForm({ ...cropForm, area_used_m2: e.target.value })} />
            </div>
            <textarea className="input" rows={2} placeholder="Notes" value={cropForm.notes} onChange={(e) => setCropForm({ ...cropForm, notes: e.target.value })} />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCropModalOpen(false)} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
              <button onClick={saveCrop} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Creer</button>
            </div>
          </div>
        </Modal>
      )}

      {/* Harvest Modal */}
      {harvestModalOpen && (
        <Modal title="Enregistrer une recolte" onClose={() => setHarvestModalOpen(false)}>
          <div className="space-y-3">
            <input className="input" type="date" value={harvestForm.harvest_date} onChange={(e) => setHarvestForm({ ...harvestForm, harvest_date: e.target.value })} />
            <input className="input" type="number" step="0.1" placeholder="Quantite (kg)" value={harvestForm.quantity_kg} onChange={(e) => setHarvestForm({ ...harvestForm, quantity_kg: e.target.value })} />
            <select className="input" value={harvestForm.quality_grade} onChange={(e) => setHarvestForm({ ...harvestForm, quality_grade: e.target.value })}>
              <option value="A">A — Premiere qualite</option>
              <option value="B">B — Seconde qualite</option>
              <option value="declasse">Declasse</option>
            </select>
            <textarea className="input" rows={2} placeholder="Notes" value={harvestForm.notes} onChange={(e) => setHarvestForm({ ...harvestForm, notes: e.target.value })} />
            <p className="text-xs text-green-600">Si la variete est liee a un produit, un mouvement de stock sera cree automatiquement.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setHarvestModalOpen(false)} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
              <button onClick={saveHarvest} className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700">Enregistrer</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ============================================================================
// TaskCard Component
// ============================================================================

interface TaskCardProps {
  task: CropTask;
  isCompleting: boolean;
  completionNote: string;
  isRescheduling: boolean;
  rescheduleDate: string;
  onStartComplete: () => void;
  onCancelComplete: () => void;
  onConfirmComplete: () => void;
  onCompletionNoteChange: (note: string) => void;
  onStartReschedule: () => void;
  onCancelReschedule: () => void;
  onConfirmReschedule: () => void;
  onRescheduleDateChange: (date: string) => void;
}

function TaskCard({
  task,
  isCompleting,
  completionNote,
  isRescheduling,
  rescheduleDate,
  onStartComplete,
  onCancelComplete,
  onConfirmComplete,
  onCompletionNoteChange,
  onStartReschedule,
  onCancelReschedule,
  onConfirmReschedule,
  onRescheduleDateChange,
}: TaskCardProps) {
  const isDone = !!task.completed_at;
  const typeColor = TASK_TYPE_COLORS[task.type] || TASK_TYPE_COLORS.OTHER;
  const typeLabel = TASK_TYPE_LABELS[task.type] || task.type;

  const varietyName = task.crop?.variety?.name || '';
  const speciesName = task.crop?.variety?.species || '';
  const plotCode = task.crop?.plot?.code || '';
  const plotName = task.crop?.plot?.name || '';

  return (
    <div
      className={`rounded-lg border p-4 transition-colors ${
        isDone
          ? 'bg-green-50 border-green-200'
          : 'bg-white border-gray-200 hover:border-gray-300'
      }`}
    >
      {/* Line 1: Status icon + Type badge + species */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {/* Status indicator */}
          <span className={`flex-shrink-0 w-3 h-3 rounded-full mt-0.5 ${isDone ? 'bg-green-500' : 'bg-gray-300'}`} />
          {/* Type badge */}
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${typeColor}`}>
            {typeLabel}
          </span>
          {/* Species + variety */}
          <span className={`text-sm font-semibold ${isDone ? 'text-green-800' : 'text-gray-900'}`}>
            {varietyName}{speciesName ? ` (${speciesName})` : ''}
          </span>
        </div>
        {/* Priority indicator */}
        {task.priority === 1 && !isDone && (
          <span className="flex-shrink-0 text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded font-medium">
            Prioritaire
          </span>
        )}
      </div>

      {/* Line 2: Plot code + zone */}
      {plotCode && (
        <div className={`mt-1 text-xs ${isDone ? 'text-green-600' : 'text-gray-500'}`}>
          Planche {plotCode}{plotName ? ` — ${plotName}` : ''}
        </div>
      )}

      {/* Line 3-4: Notes (terrain instructions) — always visible */}
      {task.notes && (
        <div className={`mt-2 text-sm leading-relaxed whitespace-pre-line rounded-md px-3 py-2 ${
          isDone ? 'bg-green-100/50 text-green-800' : 'bg-amber-50 text-gray-800 border border-amber-200'
        }`}>
          {task.notes}
        </div>
      )}

      {/* Line 5: Dates */}
      <div className={`mt-2 flex items-center gap-3 text-xs ${isDone ? 'text-green-600' : 'text-gray-500'}`}>
        {task.planned_date && (
          <span>
            Prevu : {formatDateShort(task.planned_date)}
          </span>
        )}
        {isDone && task.completed_at && (
          <span className="font-medium text-green-700">
            Fait le {formatDateOnly(task.completed_at)}
          </span>
        )}
      </div>

      {/* Line 6: Completion note (description field) */}
      {isDone && task.description && (
        <div className="mt-2 text-xs text-green-700 bg-green-100 rounded px-2 py-1 italic">
          Note : {task.description}
        </div>
      )}

      {/* Actions */}
      {!isDone && !isCompleting && !isRescheduling && (
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={onStartComplete}
            className="flex-1 sm:flex-none bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 active:bg-green-800 text-sm font-medium transition-colors min-h-[44px]"
          >
            Fait
          </button>
          <button
            onClick={onStartReschedule}
            className="px-3 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 active:bg-gray-100 text-sm text-gray-600 transition-colors min-h-[44px]"
            title="Reporter"
          >
            Reporter
          </button>
        </div>
      )}

      {/* Inline completion form */}
      {isCompleting && (
        <div className="mt-3 space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <textarea
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
            rows={2}
            placeholder="Note de terrain (optionnel)..."
            value={completionNote}
            onChange={(e) => onCompletionNoteChange(e.target.value)}
            autoFocus
          />
          <div className="flex gap-2">
            <button
              onClick={onConfirmComplete}
              className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 text-sm font-medium min-h-[44px]"
            >
              Valider
            </button>
            <button
              onClick={onCancelComplete}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm text-gray-600 min-h-[44px]"
            >
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Inline reschedule form */}
      {isRescheduling && (
        <div className="mt-3 space-y-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
          <label className="text-xs text-gray-600 font-medium">Nouvelle date :</label>
          <input
            type="date"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={rescheduleDate}
            onChange={(e) => onRescheduleDateChange(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              onClick={onConfirmReschedule}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium min-h-[44px]"
            >
              Reporter
            </button>
            <button
              onClick={onCancelReschedule}
              className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm text-gray-600 min-h-[44px]"
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Modal Component
// ============================================================================

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-semibold mb-4">{title}</h2>
        {children}
      </div>
    </div>
  );
}
